/**
 * DocsAPI office editor registration (#617) — the office-engine migration target.
 *
 * Loaded by office-registration.ts for ANY DocsAPI provider (OnlyOffice or its
 * EuroOffice fork — §10). Ids are `${idPrefix}-sheet/doc/slide` (idPrefix = the active
 * provider key) so `preferredEditors` stays meaningful and the two providers have
 * distinct persisted identities; `documentType` is injected via defaultProps so one
 * wrapper component (OnlyOfficeEditor = the shared DocsAPI editor) serves all three.
 * Collabora is the default engine and registers separately.
 */
// Office VERSION PREVIEW / read-only opens are provider-gated in
// `preview-registrations.ts`: the DocsAPI OnlyOfficeDocViewer wins under any DocsAPI
// provider, Collabora's CollabDocViewer is the default. (VersionPreview checks the
// preview registry before the editor registry, so that registration — not these
// editor entries — is what decides the preview engine.)
import { lazy } from 'react'
import { registerEditor } from '../editor-registry.js'
import { docsApiProvider } from './docsapi-provider.js'
import { DOCSAPI_EXTENSIONS } from './docsapi-document-types.js'

const LazyDocsApiEditor = lazy(() =>
  import('../../ui/OnlyOfficeEditor.js').then(m => ({ default: m.OnlyOfficeEditor }))
)

/** Register the DocsAPI office editors. Called by office-registration.ts when a
 *  DocsAPI provider is the active engine (synchronously — see there). */
export function registerDocsApiEditors(): void {
  const p = docsApiProvider()

  registerEditor({
    family: 'office-docsapi',
    notifiesFileGone: true, // #823: oo-closed / oo-readonly over the document channel
    id: `${p.idPrefix}-sheet`,
    extensions: [...DOCSAPI_EXTENSIONS.cell],
    name: `Spreadsheet Editor (${p.label})`,
    component: LazyDocsApiEditor,
    binaryExtensions: [...DOCSAPI_EXTENSIONS.cell],
    defaultProps: { documentType: 'cell' },
  })

  registerEditor({
    family: 'office-docsapi',
    notifiesFileGone: true, // #823: oo-closed / oo-readonly over the document channel
    id: `${p.idPrefix}-doc`,
    extensions: [...DOCSAPI_EXTENSIONS.word],
    name: `Document Editor (${p.label})`,
    component: LazyDocsApiEditor,
    binaryExtensions: [...DOCSAPI_EXTENSIONS.word],
    defaultProps: { documentType: 'word' },
  })

  registerEditor({
    family: 'office-docsapi',
    notifiesFileGone: true, // #823: oo-closed / oo-readonly over the document channel
    id: `${p.idPrefix}-slide`,
    extensions: [...DOCSAPI_EXTENSIONS.slide],
    name: `Presentation Editor (${p.label})`,
    component: LazyDocsApiEditor,
    binaryExtensions: [...DOCSAPI_EXTENSIONS.slide],
    defaultProps: { documentType: 'slide' },
  })
}
